import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import farmsConfig from 'config/constants/farms'
import TokensConfig from 'config/constants/tokens'
import { Signer } from 'ethers'
import {
  fetchFarmUserEarnings,
  fetchFarmUserEarningsV2,
  fetchFarmUserAllowances,
  fetchFarmUserTokenBalances,
  fetchFarmUserStakedBalances,
  fetchFarmUserStakedBalancesV2,
  fetchFarmUserPendingRewardsV2,
  fetchFarmUserPendingBoostRewards,
  fetchFarmUserPendingBoostRewardsAllFarms,
} from './fetchFarmUser'
import fetchFarmsPrices from './fetchFarmsPrices'
import fetchFarms from './fetchFarms'
import { SerializedFarmsState, SerializedFarm } from '../types'

const noAccountFarmConfig = farmsConfig.map((farm) => {
  const config = {
    ...farm,
    userData: {
      allowance: '0',
      tokenBalance: '0',
      stakedBalance: '0',
      earnings: '0',
      boostRewards: '0',
      boostRewardsAllFarms: '0',
    },
  }
  if (farm.version === 2) {
    const tokens = [TokensConfig.vvs.address]
    const earnings = ['0']

    if (farm.rewarders) {
      farm.rewarders.forEach((rewarder) => {
        tokens.push(rewarder.token.address)
        earnings.push('0')
      })
    }

    Object.assign(config.userData, { earningsV2: { tokens, earnings } })
    return config
  }
  return config
})

const initialState: SerializedFarmsState = {
  data: noAccountFarmConfig,
  loadArchivedFarmsData: false,
  userDataLoaded: false,
}

// Async thunks
export const fetchFarmsPublicDataAsync = createAsyncThunk<SerializedFarm[], number[]>(
  'farms/fetchFarmsPublicDataAsync',
  async (pids) => {
    const farmsToFetch = farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))

    const farms = await fetchFarms(farmsToFetch)
    const farmsWithPrices = await fetchFarmsPrices(farms)

    // Filter out price helper LP config farms
    const farmsWithoutHelperLps = farmsWithPrices.filter((farm: SerializedFarm) => {
      return farm.pid || farm.pid === 0
    })

    return farmsWithoutHelperLps
  },
)

interface FarmUserDataResponse {
  pid: number
  version: number
  allowance: string
  tokenBalance: string
  stakedBalance: string
  earnings: string
  boostRewards: string
  boostRewardsAllFarms: string
}

export const fetchFarmUserDataAsync = createAsyncThunk<
  FarmUserDataResponse[],
  { account: string; pids: number[]; signer?: Signer }
>('farms/fetchFarmUserDataAsync', async ({ account, pids, signer }) => {
  // v1
  const farmsToFetch = farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid) && farmConfig.version === 1)
  const userFarmAllowances = await fetchFarmUserAllowances(account, farmsToFetch)
  const userFarmTokenBalances = await fetchFarmUserTokenBalances(account, farmsToFetch)
  const userFarmStakedBalances = await fetchFarmUserStakedBalances(account, farmsToFetch)
  const userFarmEarnings = await fetchFarmUserEarnings(account, farmsToFetch)

  // v2
  const farmsToFetchV2 = farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid) && farmConfig.version === 2)
  const userFarmAllowancesV2 = await fetchFarmUserAllowances(account, farmsToFetchV2)
  const userFarmTokenBalancesV2 = await fetchFarmUserTokenBalances(account, farmsToFetchV2)
  const userFarmStakedBalancesV2 = await fetchFarmUserStakedBalancesV2(account, farmsToFetchV2)
  const userFarmEarningsV2 = await fetchFarmUserEarningsV2(account, farmsToFetchV2)
  const userFarmPendingRewardsV2 = await fetchFarmUserPendingRewardsV2(account, farmsToFetchV2)

  // User Boosts
  const [userFarmsPendingBoostRewardsV1, userFarmsPendingBoostRewardsV2, userFarmsPendingBoostRewardsAllFarms] =
    await Promise.all([
      fetchFarmUserPendingBoostRewards(farmsToFetch, signer),
      fetchFarmUserPendingBoostRewards(farmsToFetchV2, signer),
      fetchFarmUserPendingBoostRewardsAllFarms(signer),
    ])

  const userDataV1 = userFarmAllowances.map((_, index) => {
    return {
      pid: farmsToFetch[index].pid,
      version: 1,
      allowance: userFarmAllowances[index],
      tokenBalance: userFarmTokenBalances[index],
      stakedBalance: userFarmStakedBalances[index],
      earnings: userFarmEarnings[index],
      boostRewards: userFarmsPendingBoostRewardsV1[index],
      boostRewardsAllFarms: userFarmsPendingBoostRewardsAllFarms,
    }
  })
  const userDataV2 = userFarmAllowancesV2.map((_, index) => {
    return {
      pid: farmsToFetchV2[index].pid,
      version: 2,
      allowance: userFarmAllowancesV2[index],
      tokenBalance: userFarmTokenBalancesV2[index],
      stakedBalance: userFarmStakedBalancesV2[index],
      earnings: userFarmEarningsV2[index],
      earningsV2: userFarmPendingRewardsV2[index],
      boostRewards: userFarmsPendingBoostRewardsV2[index],
      boostRewardsAllFarms: userFarmsPendingBoostRewardsAllFarms,
    }
  })
  return [...userDataV1, ...userDataV2]
})

export const farmsSlice = createSlice({
  name: 'Farms',
  initialState,
  reducers: {
    setLoadArchivedFarmsData: (state, action) => {
      const loadArchivedFarmsData = action.payload
      state.loadArchivedFarmsData = loadArchivedFarmsData
    },
  },
  extraReducers: (builder) => {
    // Update farms with live data
    builder.addCase(fetchFarmsPublicDataAsync.fulfilled, (state, action) => {
      state.data = state.data.map((farm) => {
        const liveFarmData = action.payload.find(
          (farmData) => farmData.pid === farm.pid && farmData.version === farm.version,
        )
        return { ...farm, ...liveFarmData }
      })
    })

    // Update farms with user data
    builder.addCase(fetchFarmUserDataAsync.fulfilled, (state, action) => {
      action.payload.forEach((userDataEl) => {
        const { pid, version } = userDataEl
        const index = state.data.findIndex((farm) => farm.pid === pid && farm.version === version)
        state.data[index] = { ...state.data[index], userData: userDataEl }
      })
      state.userDataLoaded = true
    })
  },
})

// Actions
export const { setLoadArchivedFarmsData } = farmsSlice.actions

export default farmsSlice.reducer
